import { ApolloClient, InMemoryCache, DefaultOptions, FetchPolicy, ApolloLink, HttpLink } from "@apollo/client";
import { relayStylePagination } from "@apollo/client/utilities";
import { ASSISTANT_GRAPHQL_ENDPOINT } from "@/config/env";
import { authLink } from "./links/auth";
import { errorLink } from "./links/error";

import { parseDateValue } from "./normalizers/parseDate";

const assistantLink = new HttpLink({
  uri: ASSISTANT_GRAPHQL_ENDPOINT,
  credentials: "include",
});

const cache = new InMemoryCache({
  typePolicies: {
    Query: {
      fields: {
        // Use Apollo's built-in relay pagination helper.
        // Handles nodes/edges/pageInfo correctly, dedupes, and respects keyArgs.
        extensions: relayStylePagination(["where", "order"]),
        organizations: relayStylePagination(["where", "order"]),
        integrations: relayStylePagination(["where", "order"]),
        searchExtensions: relayStylePagination(["input", "where", "order"]),
        // Desktop app versions are returned as a list per feed. Normalize
        // DesktopAppVersion by its `version` field so InMemoryCache stores
        // array entries as references instead of plain objects. Also provide
        // a custom merge function that deduplicates entries by version and
        // treats `feed` as a key argument.
        desktopAppVersions: {
          keyArgs: ["feed"],
          merge(existing: Array<unknown> | undefined = [], incoming: Array<unknown> = []): Array<unknown> {
            // existing/incoming may be arrays of refs (strings) or objects.
            const seen = new Set<string>();
            const merged: Array<unknown> = [];

            const add = (item: unknown): void => {
              if (item === null || item === undefined) return;

              // Prefer ref format (__ref) when Apollo stores references as strings
              if (typeof item === "string") {
                if (!seen.has(item)) {
                  seen.add(item);
                  merged.push(item);
                }
                return;
              }

              if (typeof item === "object") {
                const obj = item as Record<string, unknown>;
                const typename = typeof obj.__typename === "string" ? (obj.__typename as string) : undefined;
                const version = typeof obj.version === "string" ? (obj.version as string) : undefined;

                const refId = typeof obj.__ref === "string" ? (obj.__ref as string) : typename && version ? `${typename}:${version}` : null;
                const key = refId ?? JSON.stringify(obj);
                if (!seen.has(key)) {
                  seen.add(key);
                  merged.push(obj);
                }
                return;
              }

              // Fallback for unexpected primitives
              const key = JSON.stringify(item);
              if (!seen.has(key)) {
                seen.add(key);
                merged.push(item);
              }
            };

            (existing || []).forEach(add);
            (incoming || []).forEach(add);

            return merged;
          },
        },
      },
    },
    // Normalize entity types with stable IDs and normalize DateTime fields
    ExtensionType: {
      keyFields: ["id"],
      fields: {
        createdAt: {
          read(existing) {
            return parseDateValue(existing) ?? existing;
          },
        },
        lastDownloaded: {
          read(existing) {
            return parseDateValue(existing) ?? existing;
          },
        },
      },
    },
    UserType: { keyFields: ["id"] },
    IntegrationType: { keyFields: ["id"] },
    ExtensionFrameworkType: { keyFields: ["id"] },
    Organization: {
      keyFields: ["id"],
      fields: {
        createdAt: {
          read(existing) {
            return parseDateValue(existing) ?? existing;
          },
        },
      },
    },
    ExtensionVersion: {
      keyFields: ["id"],
      fields: {
        createdAt: {
          read(existing) {
            return parseDateValue(existing) ?? existing;
          },
        },
      },
    },
    // Desktop app versions are uniquely identified by their semantic version
    // string (the `version` field) so we can normalize them across queries.
    DesktopAppVersion: { keyFields: ["version"] },
    IntegrationVersionType: {
      keyFields: ["id"],
      fields: {
        createdAt: {
          read(existing) {
            return parseDateValue(existing) ?? existing;
          },
        },
      },
    },
    StatusType: {
      keyFields: ["id"],
      fields: {
        createdAt: {
          read(existing) {
            return parseDateValue(existing) ?? existing;
          },
        },
      },
    },
    TagType: { keyFields: ["id"] },
    IconType: {
      keyFields: ["id"],
      fields: {
        createdAt: {
          read(existing) {
            return parseDateValue(existing) ?? existing;
          },
        },
      },
    },
    RoleType: { keyFields: ["id"] },
    OrganizationRole: { keyFields: ["organizationId", "userId"] },
    // Small helper types with DateTime fields
    CreateExtensionVersionStorageType: {
      keyFields: false,
      fields: {
        expirationDate: {
          read(existing) {
            return parseDateValue(existing) ?? existing;
          },
        },
      },
    },
    ExtensionVersionUriType: {
      keyFields: false,
      fields: {
        expirationDate: {
          read(existing) {
            return parseDateValue(existing) ?? existing;
          },
        },
      },
    },
    // Non-normalized types (no stable ID or embedded objects)
    ExtensionSearchResult: { keyFields: false },
  },
});

// Optimistic cache strategy: serve cached data immediately on first fetch,
// then stick to cache-first to avoid unnecessary refetches on re-renders.
// This also prevents log spam from React 18 StrictMode double-mounting.
const defaultOptions: DefaultOptions = {
  watchQuery: {
    // Initial fetch hits network and cache; subsequent watches use cache-first
    fetchPolicy: "cache-and-network" as FetchPolicy,
    nextFetchPolicy: "cache-first" as FetchPolicy,
    errorPolicy: "all",
    // Reduce intermediate renders that can multiply error logs
    notifyOnNetworkStatusChange: false,
  },
  query: {
    // Ad-hoc queries prefer cache-first to minimize network calls
    fetchPolicy: "cache-first" as FetchPolicy,
    errorPolicy: "all",
  },
  mutate: {
    // Keep errors surfaced but allow optimistic UI patterns via update functions.
    errorPolicy: "all",
  },
};

export const apolloClient = new ApolloClient({
  link: ApolloLink.from([errorLink, authLink, assistantLink]),
  cache,
  defaultOptions,
  assumeImmutableResults: true,
});

export default apolloClient;
